// This module implements a fully Bayesian approach to trendline estimation
// trendline is a simple linear regression of score on date (integer)
// prior comes from percentile rank data for the last observed score in the data array

// External Dependencies
import moment from 'moment';
import { locales } from '../constants/constants';

// Internal Dependencies
import NormService from './normService';
import store from '../store';
import progressActions from '../components/Reports/state/progress/actions';
import progressTypes from '../components/Reports/state/progress/constants';

// In-memory cache for pending requests to prevent duplicate API calls
const pendingRequests = new Map();

// Single API call function that uses Redux store for caching
async function fetchAllPeriodsData(grade, scale, locale) {
  // Get district and school IDs from store with defensive checks and convert to strings
  const rosterData = store.getState().reports.roster.data;
  let districtId;
  let schoolId;
  if (rosterData?.district?.[0]?.id) {
    districtId = String(rosterData.district[0].id);
  }
  if (rosterData?.school?.[0]?.id) {
    schoolId = String(rosterData.school[0].id);
  }
  
  const skillNameMap = {
    "ARM": "armScore",
    "armScoreAdjusted": "armScore",
    "ESRI": "esriScore",
    "PSF": "phonAwareScore",
    "ISIP": grade <= 3 ? "ISIP_overall_screener" : "ISIP_overall_ISIPAR",
    "ISIP_overall_universal": grade <= 3 ? "ISIP_overall_screener" : "ISIP_overall_ISIPAR",
    "WCPM": "wcpmScore",
    "AREA": "areaScore",
    "NWF": "decodeScore",
    "EVS": "vocabSize",
  };
  
  const skillName = skillNameMap[scale] || scale;
  
  // Get current school year from Redux store
  const assessmentCalendar = store.getState().calendar?.assessmentCalendar;
  const currentYear = assessmentCalendar?.schoolYear;
  
  // Generate cache key
  const cacheKey = `${grade}_${locale}_${districtId}_${schoolId}_${skillName}_${currentYear}`;
  
  // Check Redux store first
  const reportsState = store.getState().reports.progress;
  const cachedData = reportsState.normServiceData[cacheKey];
  
  if (cachedData) {
    return cachedData;
  }
  
  // Check if there's already a pending request for this cache key
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }
    
  // Create a promise for this request and store it
  const requestPromise = (async () => {
    try {
      const percentileValues = Array.from({length: 99}, (_, i) => i + 1);
      
      const response = await NormService.getPeriodScoreMap({
        grade: grade,
        districtId,
        schoolId,
        schoolYear: currentYear,
        skillName: skillName,
        value: percentileValues
      });
      
      // Process the response and extract all periods
      const periodNorms = response.periodScoreMap.periodNorms;
      if (!Array.isArray(periodNorms)) {
        console.warn('Invalid periodNorms structure in Norm Service response:', periodNorms);
        return {};
      }
      
      const allPeriodsData = {};
      
      // Extract data for each period
      periodNorms.forEach(norm => {
        if (norm && typeof norm.period === 'number' && Array.isArray(norm.values)) {
          allPeriodsData[norm.period] = norm.values;
        }
      });
      
      // Cache the data in Redux store
      store.dispatch(progressActions[progressTypes.PROGRESS_NORM_SERVICE_DATA](cacheKey, allPeriodsData));
      
      return allPeriodsData;
      
    } catch (error) {
      console.error(`❌ Error fetching all periods data:`, error);
      return {};
    } finally {
      // Clean up the pending request
      pendingRequests.delete(cacheKey);
    }
  })();
  
  // Store the promise in the pending requests map
  pendingRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
}

function mean(inArray){
  if(inArray.length){
    return (arraySum(inArray)/inArray.length);
  }else{
    console.warn("Tried to get mean of array of length 0");
    return 0;
  }
}

function arraySum(inArray){
  return inArray.reduce(function (total, value){
    return total+value;
  },0);
}

// standard deviation
function sd(inArray){
  if(inArray.length){
    let sumX2 = inArray.reduce(function(total,value){
      return (total+Math.pow(value,2));
    },0);
    let sum = arraySum(inArray);
    let sd = Math.sqrt((sumX2 - (Math.pow(sum,2)/inArray.length))/(inArray.length-1));
    return sd;
  }else{
    console.warn("tried to do standard deviation on array of length 0");
    return 0;
  }
}

// Pearson product-moment correlation coefficient
// inX and inY are arrays.
function cor(inX,inY){
  if(inX.length && inY.length && (inX.length === inY.length)){
    let S_x = 0.0
    let S_x2 = 0.0
    let S_y = 0.0
    let S_y2 = 0.0
    let S_xy = 0.0

    inX.reduce((total,value,index)=>{
      S_x += value;
      S_x2 += Math.pow(value,2);
      S_y += inY[index];
      S_y2 += Math.pow(inY[index],2);
      S_xy += value*inY[index];
    },0);

    try{
      let r = (S_xy - (S_x*S_y)/inX.length) / Math.sqrt((S_x2 - (Math.pow(S_x,2))/inX.length) * (S_y2 - (Math.pow(S_y,2))/inY.length));
      return r;
    }catch(err){
      console.warn("X or Y has zero variance, setting cor to 0");
      return 0.0;
    }
  }else{
    console.warn("Tried to do correlation on arrays of different lengths or length 0");
    return 0.0;
  }
}

// Regress Y on X, simple linear regression
// produce predicted values for an X array called input
// X and Y are arrays of data
function regress(Y_data,X_data,input){
  try{
    let predicted = input.map((value)=>{
      return(mean(Y_data) + 0*value);
    });
    if (sd(Y_data) > 0 && sd(X_data) > 0) {
    let slope = cor(Y_data, X_data) * sd(Y_data)/sd(X_data);
    let inter = mean(Y_data) - slope*mean(X_data);

    predicted = input.map((value)=>{
      return(inter + slope*value);
    });
    }
    return predicted;
  }catch(err){
    //If std of x or y is 0, the trend line is a horizontal with a value
    // of y = mean(y). Explicitly, it's of the form x*0+mean(Y)
    console.warn("X or Y std is 0. Using mean of Y for regression");
    predicted = input.map((value)=>{
      return(mean(Y_data) + 0*value);
    });
  }
}

// This method takes in an array of tuples and an array of dates
// and returns an array of tuples that represent the regression
// scale, period, and grade are necessary to calculate Bayesian prior
export async function calculateTrendline(datescorePairs, outputDates, scale, period, grade, locale){

  let scoreArray = [];
  //First, unpack the array of tuples into separate vectors for date and score, while
  // normalizing the dates to the output array
  let dateArray = datescorePairs.map((element)=>{
    //populate the score array here so we don't have to do another walk of the input array
    scoreArray.push(element[1]);

    // Put in terms of days since the start of the desired output array
    let daysSinceStart = moment(element[0]).diff(moment(outputDates[0]),'days');
    return daysSinceStart;
  });

  //Now, normalize the output dates vector the same way. Why do we do this? The numbers get Really big
  // if we leave in terms of the raw date and overflow some of the calculations.
  let xAxis = outputDates.map(date=>moment(date).diff(moment(outputDates[0]),'days'));

  //Get the regression line, save intercept & slope to data_coefficients
  let pred = regress(scoreArray,dateArray,xAxis);
  let data_coefficients = [pred[0], pred[1]-pred[0]];
  //pred[0] is the intercept (predicted value when xAxis==0) for the line, since the first value of dateArray is always 0
  //pred[1] - pred[0] gives us the slope of the line, as it is delta(Y) / delta(X) and delta(X) always == 1 (scale is 1 day)

  // Fetch all periods data with a single API call
  let periodData = {};
  
  try {
    periodData = await fetchAllPeriodsData(grade, scale, locale);
    
    if (Object.keys(periodData).length === 0) {
      return [];
    }
  } catch (error) {
    console.warn('Error fetching period data from Norm Service:', error);
    return [];
  }

  // Use current period data for percentile rank calculation
  const currentPeriodData = periodData[period];
  if (!currentPeriodData) {
    console.warn('No data found for current period:', period);
    return [];
  }

  // Find percentile_rank that matches most recent score (PR must be >= value in array)
  let N_points = scoreArray.length;
  let percentile_rank = 1;
  var p;
  for (p = 0; p < currentPeriodData.length; p++) {
    if (scoreArray[N_points-1] >= currentPeriodData[p]) {
      percentile_rank = p + 1;
    } else {
      break;
    }
  }

  // Build trend data from all periods
  let perc_trend_data = [];
  const periodIds = Object.keys(periodData).map(Number).sort((a, b) => a - b);
  
  if (periodIds.length > 0) {
    perc_trend_data = periodIds.map(periodId => {
      const periodValues = periodData[periodId];
      return periodValues ? periodValues[percentile_rank-1] : null;
    }).filter(value => value !== null);
  } else {
    console.warn('No period data available');
    return [];
  }

  // When student scores are regressed on days of observations, the dates are rescaled so that
  // the first activity day is 0, and the length of the pred array goes through the end of the year
  // Regression of perc_trend_data on all periods needs to have the same 0,
  // where days of the year go from 0 to 364, and the offset value is 1 less than length of xAxis
  let days_offset_value = 364 - xAxis.length - 1;
  
  // Create evenly spaced dates for all periods throughout the year
  const numPeriods = perc_trend_data.length;
  const daysPerPeriod = 364 / (numPeriods - 1);
  let centered_year_dates = [];
  for (let i = 0; i < numPeriods; i++) {
    centered_year_dates.push(i * daysPerPeriod - days_offset_value);
  }
  
  let bayes_line = regress(perc_trend_data, centered_year_dates, xAxis);

  // This adjustment to how the Bayesian coefficients are stored ensures good fit to data points, especially when scores are "up and down"
  let bayes_coefficients = [mean(scoreArray), bayes_line[1]-bayes_line[0]];

  // Now we have intercept and slope of the observed data trendline and the bayesian line (based on typical growth rate)
  // The posterior distribution of coefficients is a weighted average of the two vectors.
  // Prior (bayesian_coefficients) has weight == 364 because it contains observations over the course of year; data (data_coefficients) are weighted
  // NOT by N_points, as before, but by how many days worth of data we have. E.g., six activities on one day only count as 1, as they don't imply a trend.
  // So, with many observations OVER TIME, data have much more influence than the Prior. The weight is a proportion such that:
  // weight(data) + weight(prior) == 1. So the `weight` variable represents how much influence the data have
  let weight = dateArray[N_points-1] / (dateArray[N_points-1] + 364);
  let posterior_coefficients = [];
  posterior_coefficients[0] = weight*(data_coefficients[0]) + (1-weight)*bayes_coefficients[0];
  posterior_coefficients[1] = weight*(data_coefficients[1]) + (1-weight)*bayes_coefficients[1];

  // The new set of predictions map the original xAxis to our new posterior coefficients line,
  // which incorporates information from the data mixed with the Bayesian prior
  pred = xAxis.map((value)=>{
    return(posterior_coefficients[0] + posterior_coefficients[1]*value);
  });


  //Lastly, build the output tuples
  let outTuples = outputDates.map((date,index)=>{
    let tuple = [date];
    tuple.push(pred[index]);
    return tuple;
  });

  return outTuples;
}
